import React, { useContext, useState } from 'react';
import {
  FormWithRedirect,
  SaveButton,
  required,
  NumberInput,
  TextInput,
  useTranslate,
} from 'react-admin';
import arrayMutators from 'final-form-arrays';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import { FieldArray } from 'react-final-form-arrays';
import DialogActions from '@material-ui/core/DialogActions';
import { Box, InputAdornment } from '@material-ui/core';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import Table from '@material-ui/core/Table';
import TableHead from '@material-ui/core/TableHead';
import TableCell from '@material-ui/core/TableCell';
import TableRow from '@material-ui/core/TableRow';
import RemoveCircleOutlineIcon from '@material-ui/icons/RemoveCircleOutline';
import { QuickAppointmentContext } from '../Context';
import UnifiedAddService from '../tabs/scheduled_work/UnifiedAddService';
import UnifiedEditCoupon from '../tabs/scheduled_work/UnifiedEditCoupon';
import UnifiedEditCustomDiscount from '../tabs/scheduled_work/UnifiedEditCustomDiscount';
import useStyles from './styles';
import { areTimeSlotsEnabled } from '../helper';

const ServicesForm = () => {
  const {
    handleNext,
    vehicleData,
    serviceData,
    setServiceData,
    setScheduleData,
    couponCode,
    setCouponCode,
    customDiscountReason,
    setCustomDiscountReason,
    customDiscountAmount,
    setCustomDiscountAmount,
    setIsUsingOptimalSchedule,
  } = useContext(QuickAppointmentContext);
  const translate = useTranslate();
  const classes = useStyles();
  const [serviceOfferings, setServiceOfferings] = useState(
    serviceData?.newServices.map((s) => s.service_offering_id) || [],
  );
  const [showCouponInput, setShowCouponInput] = useState(
    couponCode || customDiscountReason || customDiscountAmount,
  );

  const isUsingTimeSlots = areTimeSlotsEnabled();

  const handleCouponChange = (event) => {
    setShowCouponInput(true);
    // Quick fix
    // ReferenceChild onChange sometimes sends the event, and sometimes sends the value.
    // (based on whether it uses SelectInput or AutocompleteInput)
    if (event?.target) {
      setCouponCode(event.target.value);
    } else {
      setCouponCode(event);
    }
  };

  const handleDiscountReasonChange = (event) => {
    setShowCouponInput(true);
    setCustomDiscountReason(event.target.value);
  };

  const handleDiscountAmountChange = (event) => {
    setShowCouponInput(true);
    setCustomDiscountAmount(parseFloat(event.target.value));
  };

  const handleSubmit = (values) => {
    setScheduleData((prev) => ({
      ...prev,
      selected_date: null,
      service_vehicle_id: null,
      service_vehicle_name: null,
    }));
    setIsUsingOptimalSchedule(!isUsingTimeSlots);
    setServiceData((prev) => ({ ...prev, ...values }));
    handleNext();
  };

  return (
    <div className={classes.main}>
      <Table className={classes.tableRoot}>
        {serviceOfferings.length > 0 && (
          <TableHead className={classes.tableRoot}>
            <TableCell className={classes.tableRoot}>
              <Box display="flex">
                <Box pr={0.8} className="editInput" width="40%">
                  <Typography variant="body2" align="center">
                    {translate('resources.service.name', { smart_count: 1 })}
                  </Typography>
                </Box>
                <Box pr={0.8} className="editInput" width="20%">
                  <Typography variant="body2" align="center">
                    {translate('resources.service.fields.labor_hours')}
                  </Typography>
                </Box>
                <Box pr={0.8} className="editInput" width="20%">
                  <Typography variant="body2" align="center">
                    {translate('resources.price.fields.labor_cost_dollars')}
                  </Typography>
                </Box>
                <Box pr={0.8} className="editInput" width="20%">
                  <Typography variant="body2" align="center">
                    {translate('resources.price.fields.parts_cost_dollars')}
                  </Typography>
                </Box>
                <Box pr={0.8} className="editInput" width="3%">
                  <Typography variant="body2">&nbsp;</Typography>
                </Box>
              </Box>
            </TableCell>
          </TableHead>
        )}

        <FormWithRedirect
          resource="customer"
          record={serviceData}
          save={handleSubmit}
          mutators={{
            ...arrayMutators,
          }}
          render={({
            handleSubmitWithRedirect,
            saving,
            form: {
              mutators: { push },
            },
          }) => (
            <>
              <TableRow className="customList">
                <TableCell colSpan={9}>
                  <FieldArray name="newServices" initialValue={serviceData}>
                    {({ fields }) =>
                      fields.map((name, index) => (
                        <Box key={name} display="flex" alignItems="flex-start">
                          <EditService
                            index={index}
                            source={name}
                            record={serviceData}
                            fields={fields}
                            setServiceOfferings={setServiceOfferings}
                          />
                        </Box>
                      ))
                    }
                  </FieldArray>
                  <Grid container spacing={1}>
                    <Grid item md={8} sm={8} xs={8} className={classes.zIndex2}>
                      <Box mt={1} className={classes.selectRoot}>
                        <UnifiedAddService
                          push={push}
                          setServiceOfferings={setServiceOfferings}
                          // pass in minimal data in place of sw for record prop. vehicleData is used to calculate extra oil cost
                          record={{ vehicle: vehicleData }}
                        />
                      </Box>
                    </Grid>
                  </Grid>
                </TableCell>
              </TableRow>
              {showCouponInput ||
              couponCode ||
              customDiscountReason ||
              customDiscountAmount ? (
                <>
                  {!couponCode &&
                    !customDiscountReason &&
                    !customDiscountAmount && (
                      <Typography
                        className={classes.clickable}
                        onClick={() => setShowCouponInput(false)}
                      >
                        <ExpandLessIcon style={{ fontSize: '14' }} />
                        Hide coupon and discount
                      </Typography>
                    )}
                  <TableRow>
                    <TableCell colSpan={1}>
                      <Grid
                        container
                        spacing={1}
                        className={`${classes.formRoot} ${classes.width100} ${classes.hideHelperText}`}
                      >
                        <Grid item md={3} sm={3} xs={12}>
                          <Typography
                            variant="body1"
                            gutterBottom
                            className={classes.listLabel}
                          >
                            {translate(
                              'resources.scheduled-work.fields.coupon_code',
                            )}
                          </Typography>
                        </Grid>
                        <Grid item md={9} sm={9} xs={12}>
                          <UnifiedEditCoupon
                            handleCouponChange={handleCouponChange}
                          />
                        </Grid>
                      </Grid>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <UnifiedEditCustomDiscount
                      showFormatted={false}
                      handleDiscountReasonChange={handleDiscountReasonChange}
                      handleDiscountAmountChange={handleDiscountAmountChange}
                    />
                  </TableRow>
                </>
              ) : (
                <Typography
                  className={classes.clickable}
                  onClick={() => setShowCouponInput(true)}
                >
                  Add a coupon or discount
                </Typography>
              )}

              <Grid container spacing={1}>
                <Grid item md={12} sm={12} xs={12}>
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    width="100%"
                  >
                    <DialogActions>
                      <SaveButton
                        className={classes.buttonWithoutIcon}
                        label="Continue"
                        handleSubmitWithRedirect={handleSubmitWithRedirect}
                        saving={saving}
                        disabled={!serviceOfferings.length}
                      />
                    </DialogActions>
                  </Box>
                </Grid>
              </Grid>
            </>
          )}
        />
      </Table>
    </div>
  );
};

export default ServicesForm;

const EditService = ({
  index,
  source: sourcePrefix,
  fields,
  setServiceOfferings,
}) => {
  const translate = useTranslate();
  const classes = useStyles();

  return (
    <>
      <Box className="serviceRow">
        <Box pr={0.8} className="editInput" width="40%">
          <TextInput
            label={translate('resources.service.fields.display_name')}
            source={`${sourcePrefix}.display_name`}
            validate={[required()]}
            className={classes.width100}
          />
        </Box>
        <Box pr={0.8} className="editInput" width="20%">
          <NumberInput
            label={translate('resources.service.fields.labor_hours')}
            source={`${sourcePrefix}.labor_hours`}
            validate={[required()]}
          />
        </Box>
        <Box pr={0.8} className="editInput" width="20%">
          <NumberInput
            label={translate('resources.service.fields.labor_cost_dollars')}
            source={`${sourcePrefix}.labor_cost_dollars`}
            validate={[required()]}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">$</InputAdornment>
              ),
            }}
          />
        </Box>
        <Box className="editInput" width="20%">
          <NumberInput
            label={translate('resources.service.fields.parts_cost_dollars')}
            source={`${sourcePrefix}.parts_cost_dollars`}
            validate={[required()]}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">$</InputAdornment>
              ),
            }}
          />
        </Box>
      </Box>
      <Box className="editInput" width="3%" mt={2.5} ml={0.63}>
        <RemoveCircleOutlineIcon
          onClick={() => {
            setServiceOfferings((so) => {
              so.splice(index, 1);
              return [...so];
            });
            fields.remove(index);
          }}
          fontSize="small"
          cursor="pointer"
        />
      </Box>
    </>
  );
};
